<script setup lang="ts">
import type { CSSProperties } from 'vue'
import type { ITextStyle } from '@/components/Text/index.vue'

export type HeadingLevel = typeof possibleHeadingLevels[number]

interface IProps {
  level?: HeadingLevel
  color?: string
  maxLines?: number
  headingStyles?: CSSProperties
  isGradient?: boolean
  bold?: boolean
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    level: '1',
    isGradient: false,
    color: 'inherit',
  },
)
const { level, maxLines, headingStyles, isGradient, bold } = toRefs(props)

const { colors, fontSizes } = useTheme()

const styles: Record<HeadingLevel, Omit<ITextStyle, 'fontSize'>> = {
  '1': {
    fontFamily: 'Roboto',
    fontWeight: 600,
    lineHeight: '38px',
  },
  '2': {
    fontFamily: 'Roboto',
    fontWeight: 600,
    lineHeight: '28px',
  },
  '2-light': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '28px',
    color: colors.softBlack,
  },
  '3': {
    fontFamily: 'Roboto',
    fontWeight: 600,
    lineHeight: '24px',
  },
  '3-light': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '24px',
  },
  '4': {
    fontFamily: 'Roboto',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '-0.02em',
  },
  '4-light': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '22px',
    letterSpacing: '-0.02em',
  },
  '5': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '18px',
  },
  '5-light': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '18px',
  },
  '6': {
    fontFamily: 'Roboto',
    fontWeight: 500,
    lineHeight: '14px',
  },
}

const componentStyles = computed<CSSProperties>(() => {
  const baseStyles: CSSProperties = {
    ...styles[level.value],
    'fontSize': fontSizes[`heading-${level.value}`],
    'overflow': maxLines?.value ? 'hidden' : 'visible',
    'textOverflow': maxLines?.value ? 'ellipsis' : 'visible',
    'display': '-webkit-box',
    '-webkit-line-clamp': maxLines?.value,
    '-webkit-box-orient': 'vertical',
    'wordBreak': 'break-word',
    ...headingStyles?.value,
  }

  if (bold.value)
    baseStyles.fontWeight = 700

  if (isGradient.value) {
    const gradientStyles: CSSProperties = {
      background: 'linear-gradient(249.77deg, #35DBFF -2.27%, #B09EF8 43.11%, #FD93E7 100.75%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    }

    return {
      ...baseStyles,
      ...gradientStyles,
    }
  }

  return baseStyles
})
</script>

<script lang="ts">
export const possibleHeadingLevels = ['1', '2', '2-light', '3', '3-light', '4', '4-light', '5', '5-light', '6'] as const
</script>

<template>
  <Component
    :is="`h${level}`"
    class="heading"
    text-black
    font-semibold
    :style="{
      ...componentStyles,
      color,
    }"
  >
    <slot />
  </Component>
</template>

<style scoped>
.heading {
  font-family: 'Roboto', sans-serif;
}
</style>
